import type { NotificationsRepository } from "@estelle/domain";

import { THEME_STORAGE_KEY } from "@/lib/theme";
import { unsubscribeThisDevice } from "@/lib/pwa/push";

/**
 * What signing out removes from the device itself (E6, #446, slice 5): "sign-out
 * clears browser-held state".
 *
 * Two halves, because the first needs the session and the second must come
 * after it ends:
 *
 *  1. `releaseThisDevice`, **before** the session ends: unsubscribes this
 *     browser from push and tells the API to forget it, so the next person to
 *     use this browser does not get the last one's notifications. The API call
 *     needs the cookie, which is why it cannot wait.
 *  2. `clearBrowserState`, **after**: deletes every Cache Storage entry (the
 *     service worker only ever put the app shell in it, but nothing is left
 *     behind to be argued about) and everything this app kept in local and
 *     session storage — saved column choices, the last warehouse, the open
 *     sidebar groups — except the theme, which is a taste, not a record of what
 *     the person was doing.
 *
 * Neither may keep someone from signing out: each swallows its own failure and
 * gives up after a few seconds. The cache the React Query client holds is
 * `forgetCacheAfterSignOut`'s business, not this file's.
 */

const GIVE_UP_MS = 3000;

function withTimeout<T>(work: Promise<T>): Promise<T | undefined> {
  return Promise.race([
    work,
    new Promise<undefined>((resolve) => setTimeout(resolve, GIVE_UP_MS)),
  ]);
}

export async function releaseThisDevice(
  notifications: Pick<NotificationsRepository, "removePushDevice">,
): Promise<void> {
  try {
    await withTimeout(
      (async () => {
        const endpoint = await unsubscribeThisDevice();
        if (endpoint) await notifications.removePushDevice({ endpoint });
      })(),
    );
  } catch {
    // Signing out comes first. A device the API still lists is removed the
    // next time a push to it is refused, and by the person on the card.
  }
}

export async function clearBrowserState(): Promise<void> {
  try {
    if ("caches" in window) {
      const names = await withTimeout(caches.keys());
      await Promise.all((names ?? []).map((name) => caches.delete(name)));
    }
  } catch {
    // Cache Storage can be blocked outright (private windows, site settings).
  }

  try {
    sessionStorage.clear();
  } catch {
    // Same for storage: nothing to clear if it cannot be read.
  }
  try {
    const keys: string[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key !== null && key !== THEME_STORAGE_KEY) keys.push(key);
    }
    for (const key of keys) localStorage.removeItem(key);
  } catch {
    // As above.
  }
}
